import { useEffect } from "preact/hooks";
import {
  getIngestBaseUrl,
  getManagementApiBaseUrl,
} from "../lib/public-url.js";
import PublicHeader from "../components/PublicHeader.js";
import PublicFooter from "../components/PublicFooter.js";

// ---------------------------------------------------------------------------
// Shared UI helpers
// ---------------------------------------------------------------------------

function CodeBlock({ children }: { children: string }) {
  return (
    <pre class="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-800">
      <code>{children.trim()}</code>
    </pre>
  );
}

function Badge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: "bg-emerald-100 text-emerald-800",
    POST: "bg-blue-100 text-blue-800",
    PATCH: "bg-amber-100 text-amber-800",
    DELETE: "bg-red-100 text-red-800",
  };
  return (
    <span
      class={`inline-block rounded px-2 py-0.5 text-xs font-bold uppercase ${colors[method] ?? "bg-slate-100 text-slate-700"}`}
    >
      {method}
    </span>
  );
}

function Endpoint({
  method,
  path,
  description,
  auth,
  children,
}: {
  method: string;
  path: string;
  description: string;
  auth: string;
  children?: preact.ComponentChildren;
}) {
  return (
    <div class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <div class="flex flex-wrap items-center gap-3">
        <Badge method={method} />
        <code class="text-base font-semibold text-slate-900">{path}</code>
        <span class="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-500">
          {auth}
        </span>
      </div>
      <p class="mt-2 text-sm text-slate-600">{description}</p>
      {children && <div class="mt-4 space-y-4">{children}</div>}
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: preact.ComponentChildren;
}) {
  return (
    <section id={id} class="scroll-mt-20">
      <h2 class="mb-6 text-2xl font-bold tracking-tight text-slate-950">
        {title}
      </h2>
      <div class="space-y-6">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sidebar navigation
// ---------------------------------------------------------------------------

const navSections = [
  { id: "overview", label: "Overview" },
  { id: "authentication", label: "Authentication" },
  { id: "static-ip", label: "Static IP & whitelisting" },
  { id: "ingest", label: "Webhook ingest" },
  { id: "outbound", label: "Outbound notifications" },
  { id: "signing-keys", label: "Signing keys" },
  { id: "endpoints", label: "Endpoints" },
  { id: "secrets", label: "Endpoint secrets" },
  { id: "events", label: "Events & retrieval" },
  { id: "api-keys", label: "API keys" },
  { id: "billing", label: "Billing & usage" },
  { id: "errors", label: "Error handling" },
  { id: "rate-limiting", label: "Rate limiting" },
];


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Docs() {
  const managementApiBaseUrl = getManagementApiBaseUrl();
  const ingestBaseUrl = getIngestBaseUrl();
  const ingestDomain = new URL(ingestBaseUrl).host;

  useEffect(() => {
    document.title = "API Documentation — webhookstorage";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Complete API reference for webhookstorage.com — webhook ingest, endpoint management, API keys, billing, and outbound notification format.",
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://webhookstorage.com/docs");
  }, []);

  return (
    <div class="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900">
      {/* Header */}
      <div class="sticky top-0 z-30">
        <PublicHeader current="docs" />
      </div>

      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="lg:grid lg:grid-cols-[14rem_1fr] lg:gap-10">
          {/* Sidebar */}
          <nav class="hidden py-10 lg:block">
            <div class="sticky top-20 space-y-1">
              <p class="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                API Reference
              </p>
              {navSections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  class="block rounded-md px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-100 hover:text-slate-950"
                >
                  {s.label}
                </a>
              ))}
            </div>
          </nav>

          {/* Main content */}
          <main class="max-w-3xl py-10 lg:border-l lg:border-slate-200/60 lg:pl-10">
            <div class="space-y-16">
              {/* Overview */}
              <Section id="overview" title="API overview">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    webhookstorage provides a REST API for managing webhook
                    endpoints, API keys, and retrieving usage data. There are two
                    separate base URLs:
                  </p>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Management API
                      </p>
                      <code class="mt-1 block text-sm font-semibold text-slate-900">
                        {managementApiBaseUrl}
                      </code>
                      <p class="mt-2 text-xs text-slate-500">
                        Endpoints, API keys, billing, auth
                      </p>
                    </div>
                    <div class="rounded-lg border border-teal-200 bg-teal-50 p-4">
                      <p class="text-xs font-semibold uppercase tracking-wider text-teal-700">
                        Ingest endpoint
                      </p>
                      <code class="mt-1 block text-sm font-semibold text-slate-900">
                        {ingestBaseUrl}
                      </code>
                      <p class="mt-2 text-xs text-teal-600">
                        Static IP — send webhooks here
                      </p>
                    </div>
                  </div>
                  <p>
                    Management API responses use <code>Content-Type: application/json</code>
                    . Dates are ISO 8601. IDs use prefixed formats:{" "}
                    <code>ep_</code> for endpoints, <code>sec_</code> for
                    secrets. API key IDs use the <code>key_</code> prefix.
                    Event IDs are bare UUIDs.
                  </p>
                  <div class="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-900">
                    An OpenAPI 3.1 specification is available at{" "}
                    <code class="rounded bg-teal-100 px-1 text-xs">/v1/openapi.json</code>{" "}
                    for client code generation and contract testing.
                  </div>
                  <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    Service status and incident history:{" "}
                    <a
                      href="https://status.webhookstorage.com"
                      class="font-medium text-teal-700 underline hover:text-teal-900"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      status.webhookstorage.com
                    </a>
                  </div>
                </div>
              </Section>

              {/* Authentication */}
              <Section id="authentication" title="Authentication">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    webhookstorage uses three authentication models depending
                    on what you are doing:
                  </p>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <div class="rounded-lg border border-teal-200 p-4">
                      <p class="font-semibold text-slate-900">
                        Endpoint secret
                      </p>
                      <p class="mt-1 text-xs text-teal-600">
                        For sending webhooks ({ingestDomain})
                      </p>
                      <CodeBlock>
                        {`Authorization: Bearer <endpoint_secret>
# compatibility fallback only:
?secret=<endpoint_secret>`}
                      </CodeBlock>
                      <p class="mt-2 text-xs text-slate-600">
                        Each endpoint gets its own secret (prefixed{" "}
                        <code>whse_</code>). You give this secret to the
                        webhook sender — it authenticates each ingest request.
                        See{" "}
                        <a href="#secrets" class="text-teal-700 underline">
                          Endpoint secrets
                        </a>{" "}
                        for rotation and management.
                      </p>
                      <p class="mt-2 text-xs text-slate-600">
                        Prefer the <code>Authorization</code> header.
                        Query-string secrets can appear in access logs, so
                        reserve <code>?secret=</code> for senders that cannot
                        set custom headers.
                      </p>
                    </div>
                    <div class="rounded-lg border border-slate-200 p-4">
                      <p class="font-semibold text-slate-900">
                        Session token
                      </p>
                      <p class="mt-1 text-xs text-slate-500">
                        For dashboard-authenticated requests — all management
                        API routes
                      </p>
                      <CodeBlock>
                        {`Authorization: Bearer <access_token>`}
                      </CodeBlock>
                      <p class="mt-2 text-xs text-slate-600">
                        Session tokens are issued automatically when you sign in
                        through the dashboard. You do not need to manage them
                        directly — the dashboard handles token refresh and
                        logout.
                      </p>
                    </div>
                    <div class="rounded-lg border border-slate-200 p-4">
                      <p class="font-semibold text-slate-900">
                        API key
                      </p>
                      <p class="mt-1 text-xs text-slate-500">
                        For programmatic access to endpoints, events, secrets,
                        and billing
                      </p>
                      <CodeBlock>
                        {`Authorization: Bearer <api_key>`}
                      </CodeBlock>
                      <p class="mt-2 text-xs text-slate-600">
                        API keys (prefixed <code>wos_</code>) provide full
                        programmatic access to endpoint CRUD, secret management,
                        event retrieval, and billing summary. Create and manage
                        them in the dashboard or via the{" "}
                        <a href="#api-keys" class="text-teal-700 underline">
                          API keys
                        </a>{" "}
                        endpoints.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p class="text-sm font-semibold text-slate-900">
                    Payload retrieval
                  </p>
                  <p class="mt-2 text-sm leading-7 text-slate-700">
                    When your automation platform receives a notification, it
                    includes a <code>download_url</code> — a presigned S3 link
                    valid for 15 minutes. Fetching the payload is a simple HTTP
                    GET with no additional authentication. The signature in the
                    URL handles authorization. You can also retrieve download
                    URLs later through the{" "}
                    <a href="#events" class="text-teal-700 underline">
                      Events API
                    </a>
                    .
                  </p>
                </div>
              </Section>

              {/* Static IP & Whitelisting */}
              <Section id="static-ip" title="Static IP & whitelisting">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    All inbound webhook traffic reaches webhookstorage through a
                    single static IP address. This IP is tied to an AWS Elastic
                    IP behind a Network Load Balancer and will not change across
                    deployments.
                  </p>
                </div>

                <div class="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <p>
                    The static IP address, CIDR notation, and hostname are shown
                    in your{" "}
                    <a href="/dashboard" class="font-medium text-teal-700 underline">
                      dashboard
                    </a>{" "}
                    after sign-up. Port:{" "}
                    <code class="rounded bg-slate-100 px-1">443</code> (HTTPS
                    only).
                  </p>
                </div>

                <div class="space-y-3">
                  <h3 class="text-base font-semibold text-slate-900">
                    When to whitelist
                  </h3>
                  <ul class="list-disc space-y-2 pl-5 text-sm leading-7 text-slate-700">
                    <li>
                      <strong>Source platform allowlisting:</strong> Some
                      platforms (e.g. Shopify) let you restrict which IPs can
                      receive webhook callbacks. Add the ingest IP to those
                      allowlists so the platform recognizes webhookstorage as a
                      valid destination.
                    </li>
                    <li>
                      <strong>Outbound notifications:</strong> If your
                      downstream automation (Zapier, Make, n8n) sits behind a
                      firewall or WAF, allow the outbound delivery IP so
                      webhookstorage can reach your notification URL. Both IPs
                      are shown in your dashboard.
                    </li>
                    <li>
                      <strong>Audit & compliance:</strong> Static IPs simplify
                      security reviews — unchanging addresses rather than
                      dynamic cloud ranges.
                    </li>
                  </ul>
                </div>

                <div class="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  <strong>Stable guarantee:</strong> Both IPs are dedicated
                  Elastic IPs that persist across deployments and restarts. If
                  either changes (highly unlikely), we will provide 90 days'
                  advance notice via email and in-dashboard notification.
                </div>
              </Section>

              {/* Webhook Ingest */}
              <Section id="ingest" title="Webhook ingest">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    The ingest endpoint accepts webhook payloads on a dedicated
                    static IP. Point upstream senders here.
                    Payloads are stored in S3 and a lightweight notification is
                    sent to your configured outbound URL.
                  </p>
                </div>

                <Endpoint
                  method="POST"
                  path="/:endpointId"
                  description="Ingest a webhook payload. The body is stored in S3 and an outbound notification is dispatched."
                  auth="Endpoint secret"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Headers
                  </p>
                  <CodeBlock>
                    {`Authorization: Bearer <endpoint_secret>
Content-Type: application/json  # or any MIME type
X-Idempotency-Key: string      # optional (defaults to SHA-256 of body)`}
                  </CodeBlock>
                  <p class="text-sm text-slate-600">
                    If your webhook source cannot send custom headers, you can
                    fall back to{" "}
                    <code>?secret=&lt;endpoint_secret&gt;</code>. Treat that as
                    a compatibility path only because full URLs may be captured
                    by upstream access logs.
                  </p>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    URL parameters
                  </p>
                  <CodeBlock>
                    {`endpointId  string  Required. Your endpoint ID (ep_...)`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Request body
                  </p>
                  <p class="text-sm text-slate-600">
                    Any binary or text data, up to 250 MB.
                  </p>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "event_id": "550e8400-e29b-41d4-a716-446655440000",
  "endpoint_id": "ep_a1b2c3d4e5f6",
  "stored_at": "2025-03-09T12:00:00.000Z",
  "size_bytes": 15728640
}`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Duplicate detection · 200
                  </p>
                  <CodeBlock>
                    {`{
  "event_id": "550e8400-e29b-41d4-a716-446655440000",
  "endpoint_id": "ep_a1b2c3d4e5f6",
  "duplicate": true
}`}
                  </CodeBlock>
                  <p class="text-sm text-slate-600">
                    Duplicates are detected within a 7-day window using the{" "}
                    <code>X-Idempotency-Key</code> header or SHA-256 of the
                    body.
                  </p>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Error responses
                  </p>
                  <div class="overflow-x-auto">
                    <table class="w-full text-sm">
                      <thead>
                        <tr class="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                          <th class="pb-2 pr-4">Status</th>
                          <th class="pb-2 pr-4">Code</th>
                          <th class="pb-2">When</th>
                        </tr>
                      </thead>
                      <tbody class="text-slate-700">
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">400</td>
                          <td class="py-2 pr-4">VALIDATION_FAILED</td>
                          <td class="py-2">Empty payload or invalid body (ingest-specific)</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">401</td>
                          <td class="py-2 pr-4">UNAUTHORIZED</td>
                          <td class="py-2">Missing or invalid secret</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">402</td>
                          <td class="py-2 pr-4">BILLING_BLOCKED</td>
                          <td class="py-2">Account billing blocked (past-due activated tenant)</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">402</td>
                          <td class="py-2 pr-4">FREE_TIER_LIMIT_REACHED</td>
                          <td class="py-2">
                            Free tier monthly cap exhausted. Response{" "}
                            <code>error.details.upgrade_url</code> points to the
                            activation page.
                          </td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">403</td>
                          <td class="py-2 pr-4">IP_DENIED</td>
                          <td class="py-2">Source IP not in endpoint allowlist</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">403</td>
                          <td class="py-2 pr-4">FORBIDDEN</td>
                          <td class="py-2">Account disabled</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">404</td>
                          <td class="py-2 pr-4">NOT_FOUND</td>
                          <td class="py-2">Endpoint does not exist or is inactive</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">410</td>
                          <td class="py-2 pr-4">GONE</td>
                          <td class="py-2">Tenant cancelled or deactivated</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">413</td>
                          <td class="py-2 pr-4">PAYLOAD_TOO_LARGE</td>
                          <td class="py-2">Exceeds tenant cap or 250 MB hard limit</td>
                        </tr>
                        <tr class="border-b border-slate-100">
                          <td class="py-2 pr-4 font-mono">429</td>
                          <td class="py-2 pr-4">RATE_LIMITED</td>
                          <td class="py-2">
                            Rate limit exceeded (includes{" "}
                            <code>Retry-After</code> header)
                          </td>
                        </tr>
                        <tr>
                          <td class="py-2 pr-4 font-mono">503</td>
                          <td class="py-2 pr-4">SERVICE_UNAVAILABLE</td>
                          <td class="py-2">Tenant is being provisioned</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/:endpointId?challenge=..."
                  description="Challenge-response verification for webhook providers that require URL ownership proof."
                  auth="Public"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <p class="text-sm text-slate-600">
                    Returns the <code>challenge</code> value as plain text.
                  </p>
                </Endpoint>
              </Section>

              {/* Outbound Notifications */}
              <Section id="outbound" title="Outbound notifications">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    After a successful ingest, webhookstorage sends a
                    lightweight JSON POST to the endpoint's configured{" "}
                    <code>outbound_url</code>. This is what your automation
                    platform (Zapier, Make, n8n) receives.
                  </p>
                </div>

                <div class="rounded-xl border border-teal-200 bg-teal-50/50 p-6 shadow-sm">
                  <p class="text-xs font-semibold uppercase tracking-wider text-teal-700">
                    Notification payload
                  </p>
                  <p class="mt-2 text-sm text-slate-600">
                    Your outbound URL receives a POST with these headers and
                    body:
                  </p>
                  <div class="mt-4">
                    <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Headers
                    </p>
                    <CodeBlock>
                      {`Content-Type: application/json
X-Webhookstorage-Signature: ed25519=<base64url_signature>,keyId=key-1
X-Webhookstorage-Timestamp: 1710000000`}
                    </CodeBlock>
                  </div>
                  <div class="mt-4">
                    <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Body
                    </p>
                    <CodeBlock>
                      {`{
  "event_id": "550e8400-e29b-41d4-a716-446655440000",
  "endpoint_id": "ep_a1b2c3d4e5f6",
  "size_bytes": 15728640,
  "content_type": "image/png",
  "received_at": "2025-03-09T12:00:00.000Z",
  "download_url": "https://s3.amazonaws.com/...?X-Amz-Signature=..."
}`}
                    </CodeBlock>
                  </div>
                  <div class="mt-4 space-y-2 text-sm text-slate-700">
                    <p>
                      <strong>download_url</strong> — Presigned S3 URL valid for
                      15 minutes. Your automation fetches the full payload
                      through this link.
                    </p>
                    <p>
                      <strong>Signature verification</strong> — Ed25519
                      signature over{" "}
                      <code>{"{timestamp}.{raw_json_body}"}</code>. Use the{" "}
                      <code>X-Webhookstorage-Signature</code> header to verify
                      authenticity.
                    </p>
                    <p>
                      <strong>Delivery</strong> — Dispatched via an ordered
                      message queue for at-least-once delivery. Failed
                      deliveries are retried up to 3 times.
                    </p>
                    <p>
                      <strong>Public key discovery</strong> — Fetch the
                      Ed25519 public key for verifying signatures from{" "}
                      <code>GET /v1/signing-keys</code>. The response includes
                      the key in PEM format, keyed by <code>key_id</code>.
                      Match the <code>keyId</code> from the signature header
                      to the correct key. This endpoint is public, cacheable,
                      and supports key rotation.
                    </p>
                  </div>
                </div>
              </Section>

              {/* Signing Keys */}
              <Section id="signing-keys" title="Signing keys">
                <p class="text-sm leading-7 text-slate-700">
                  Public endpoint for retrieving the Ed25519 public key used to
                  verify{" "}
                  <code>X-Webhookstorage-Signature</code> headers on outbound
                  notifications.
                </p>

                <Endpoint
                  method="GET"
                  path="/v1/signing-keys"
                  description="Returns the Ed25519 public key(s) used to verify outbound notification signatures."
                  auth="Public"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "keys": [
    {
      "key_id": "key-1",
      "algorithm": "ed25519",
      "public_key_pem": "-----BEGIN PUBLIC KEY-----\\n..."
    }
  ]
}`}
                  </CodeBlock>
                  <div class="mt-2 space-y-1 text-xs text-slate-500">
                    <p>
                      Match the <code>keyId</code> from the{" "}
                      <code>X-Webhookstorage-Signature</code> header to the
                      corresponding <code>key_id</code> in the response.
                    </p>
                    <p>
                      Response is cacheable:{" "}
                      <code>Cache-Control: public, max-age=3600</code>.
                    </p>
                    <p>
                      No authentication required. Supports key rotation — the
                      array may contain multiple keys during a rollover period.
                    </p>
                  </div>
                </Endpoint>
              </Section>

              {/* Endpoints */}
              <Section id="endpoints" title="Endpoints">
                <p class="text-sm leading-7 text-slate-700">
                  Webhook endpoints define where upstream senders POST payloads.
                  Each endpoint has its own secrets, outbound URL, and event
                  history.
                </p>

                <Endpoint
                  method="POST"
                  path="/v1/endpoints"
                  description="Create a new webhook endpoint with an auto-generated secret."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Request body
                  </p>
                  <CodeBlock>
                    {`{
  "label": "Production uploads",
  "outbound_url": "https://hook.us1.make.com/abc..."
}`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 201
                  </p>
                  <CodeBlock>
                    {`{
  "endpoint_id": "ep_a1b2c3d4e5f6",
  "label": "Production uploads",
  "outbound_url": "https://hook.us1.make.com/abc...",
  "status": "active",
  "created_at": "2025-03-09T12:00:00.000Z",
  "secret": {
    "secret_id": "sec_x1y2z3",
    "plaintext": "whse_live_AbCdEf..."
  }
}`}
                  </CodeBlock>
                  <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <strong>Important:</strong> The secret{" "}
                    <code>plaintext</code> is retrievable for 5 minutes after
                    creation. After that, only the masked hint is available.
                    Copy it promptly.
                  </div>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/v1/endpoints"
                  description="List all active endpoints for your account."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "endpoints": [
    {
      "endpoint_id": "ep_a1b2c3d4e5f6",
      "label": "Production uploads",
      "outbound_url": "https://hook.us1.make.com/abc...",
      "status": "active",
      "created_at": "2025-03-09T12:00:00.000Z"
    }
  ]
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/v1/endpoints/:id"
                  description="Get full endpoint details including all secrets (masked)."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "endpoint_id": "ep_a1b2c3d4e5f6",
  "label": "Production uploads",
  "outbound_url": "https://hook.us1.make.com/abc...",
  "status": "active",
  "created_at": "2025-03-09T12:00:00.000Z",
  "secrets": [
    {
      "secret_id": "sec_x1y2z3",
      "hint": "whse_••••••••abcd",
      "status": "active",
      "created_at": "2025-03-09T12:00:00.000Z"
    }
  ]
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="PATCH"
                  path="/v1/endpoints/:id"
                  description="Update an endpoint's label, outbound URL, or IP allowlist."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Request body
                  </p>
                  <CodeBlock>
                    {`{
  "label": "Updated label",
  "outbound_url": "https://new-hook-url.com/...",
  "ip_allowlist": ["203.0.113.0/24"]
}`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "updated": true,
  "id": "ep_a1b2c3d4e5f6"
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="DELETE"
                  path="/v1/endpoints/:id"
                  description="Soft-delete an endpoint. It will stop accepting webhooks."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "deleted": true,
  "id": "ep_a1b2c3d4e5f6"
}`}
                  </CodeBlock>
                </Endpoint>
              </Section>

              {/* Endpoint Secrets */}
              <Section id="secrets" title="Endpoint secrets">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    Each endpoint can have up to 2 active secrets. This allows
                    zero-downtime secret rotation: create a new secret, update
                    the sender, then revoke the old one.
                  </p>
                  <p>
                    Secrets use the <code>whse_</code> prefix. They are stored as
                    SHA-256 hashes — the plaintext is retrievable for 5 minutes
                    after creation, after which only the masked hint is
                    available.
                  </p>
                </div>

                <Endpoint
                  method="POST"
                  path="/v1/endpoints/:id/secrets"
                  description="Create a new secret for an endpoint (max 2 active)."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Request body
                  </p>
                  <CodeBlock>
                    {`{
  "label": "Rotated March 2025"
}`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 201
                  </p>
                  <CodeBlock>
                    {`{
  "secret_id": "sec_n3w1d",
  "label": "Rotated March 2025",
  "plaintext": "whse_live_NewSecretValue...",
  "plaintext_expires_at": "2025-03-09T12:05:00.000Z",
  "created_at": "2025-03-09T12:00:00.000Z"
}`}
                  </CodeBlock>
                  <p class="text-xs text-slate-500">
                    <code>plaintext_expires_at</code> — ISO 8601 timestamp
                    indicating when the plaintext secret will no longer be
                    retrievable (5 minutes after creation).
                  </p>
                  <p class="text-xs text-slate-500">
                    Returns <code>409 CONFLICT</code> if 2 active secrets
                    already exist. Revoke one first.
                  </p>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/v1/endpoints/:id/secrets"
                  description="List all secrets (active and revoked) for an endpoint."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "secrets": [
    {
      "secret_id": "sec_x1y2z3",
      "hint": "whse_••••••••abcd",
      "label": "Original",
      "status": "active",
      "created_at": "2025-03-01T00:00:00.000Z"
    },
    {
      "secret_id": "sec_n3w1d",
      "hint": "whse_••••••••efgh",
      "label": "Rotated March 2025",
      "status": "revoked",
      "created_at": "2025-03-09T00:00:00.000Z"
    }
  ]
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/v1/endpoints/:id/secrets/:secretId"
                  description="Get a single secret. Includes plaintext if within 5 minutes of creation."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "secret_id": "sec_x1y2z3",
  "hint": "whse_••••••••abcd",
  "label": "Original",
  "status": "active",
  "created_at": "2025-03-01T00:00:00.000Z",
  "plaintext": "whse_live_AbCdEf...",
  "plaintext_expires_at": "2025-03-01T00:05:00.000Z"
}`}
                  </CodeBlock>
                  <p class="text-xs text-slate-500">
                    <code>plaintext</code> and{" "}
                    <code>plaintext_expires_at</code> are only present within
                    5 minutes of creation.
                  </p>
                </Endpoint>

                <Endpoint
                  method="DELETE"
                  path="/v1/endpoints/:id/secrets/:secretId"
                  description="Revoke a secret. Senders using it will get 401 on ingest."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "revoked": true,
  "id": "sec_x1y2z3"
}`}
                  </CodeBlock>
                </Endpoint>
              </Section>

              {/* Events & Retrieval */}
              <Section id="events" title="Events & retrieval">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    Every accepted webhook is stored as an event. Use these
                    endpoints to list events for an endpoint or retrieve a
                    specific event with a fresh download URL.
                  </p>
                  <p>
                    Each event includes a <code>download_url</code> — a
                    presigned S3 link valid for 15 minutes. Fetch the payload
                    with a simple HTTP GET; no additional authentication is
                    needed.
                  </p>
                </div>

                <Endpoint
                  method="GET"
                  path="/v1/endpoints/:endpointId/events"
                  description="List stored events for an endpoint, newest first."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Query parameters
                  </p>
                  <CodeBlock>
                    {`limit   integer  (optional, default 25, max 100)
cursor  string   (optional, for pagination)`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "events": [
    {
      "event_id": "550e8400-e29b-41d4-a716-446655440000",
      "endpoint_id": "ep_a1b2c3d4e5f6",
      "size_bytes": 15728640,
      "content_type": "application/json",
      "source_ip": "203.0.113.42",
      "is_test": false,
      "received_at": "2026-03-09T12:00:00.000Z",
      "timestamp": "2026-03-09T12:00:00.000Z",
      "expires_at": "2026-03-16T12:00:00.000Z",
      "download_url": "https://s3.amazonaws.com/...?X-Amz-Signature=..."
    }
  ],
  "cursor": "eyJway...",
  "next_cursor": "eyJway..."
}`}
                  </CodeBlock>
                  <div class="mt-2 space-y-1 text-xs text-slate-500">
                    <p>
                      <code>source_ip</code> — IP address of the webhook sender
                      (or <code>"unknown"</code> if unavailable).
                    </p>
                    <p>
                      <code>timestamp</code> — ISO 8601 alias for{" "}
                      <code>received_at</code>.
                    </p>
                  </div>
                  <p class="text-xs text-slate-500">
                    Pass <code>cursor</code> (or <code>next_cursor</code>) as a
                    query parameter to fetch the next page.
                  </p>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/v1/endpoints/:endpointId/events/:eventId"
                  description="Get a single event with a fresh download URL."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "event_id": "550e8400-e29b-41d4-a716-446655440000",
  "endpoint_id": "ep_a1b2c3d4e5f6",
  "size_bytes": 15728640,
  "content_type": "application/json",
  "source_ip": "203.0.113.42",
  "is_test": false,
  "received_at": "2026-03-09T12:00:00.000Z",
  "timestamp": "2026-03-09T12:00:00.000Z",
  "expires_at": "2026-03-16T12:00:00.000Z",
  "download_url": "https://s3.amazonaws.com/...?X-Amz-Signature=..."
}`}
                  </CodeBlock>
                  <div class="mt-2 space-y-1 text-xs text-slate-500">
                    <p>
                      <code>source_ip</code> — IP address of the webhook sender
                      (or <code>"unknown"</code> if unavailable).
                    </p>
                    <p>
                      <code>timestamp</code> — ISO 8601 alias for{" "}
                      <code>received_at</code>.
                    </p>
                  </div>
                  <p class="text-xs text-slate-500">
                    Returns <code>404 NOT_FOUND</code> if the event or
                    endpoint does not exist.
                  </p>
                </Endpoint>

                <Endpoint
                  method="DELETE"
                  path="/v1/endpoints/:endpointId/events/:eventId"
                  description="Permanently delete an event and its stored payload."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "deleted": true,
  "id": "550e8400-e29b-41d4-a716-446655440000"
}`}
                  </CodeBlock>
                </Endpoint>
              </Section>

              {/* API Keys */}
              <Section id="api-keys" title="API keys">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    API keys provide programmatic access to the management
                    API. They can create, update, and delete endpoints, manage
                    secrets, browse stored events, retrieve download URLs, and
                    read billing summaries.
                  </p>
                </div>

                <Endpoint
                  method="POST"
                  path="/v1/keys"
                  description="Create a new API key."
                  auth="JWT"
                >
                  <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                    Dashboard session only — not available via API key
                    authentication.
                  </div>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Request body
                  </p>
                  <CodeBlock>
                    {`{
  "label": "CI/CD pipeline"
}`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 201
                  </p>
                  <CodeBlock>
                    {`{
  "key_id": "key_a1b2c3d4",
  "label": "CI/CD pipeline",
  "plaintext": "wos_live_aBcDeFgH...",
  "created_at": "2025-03-09T12:00:00.000Z"
}`}
                  </CodeBlock>
                  <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <strong>Important:</strong> The <code>plaintext</code> key is
                    shown only once. Store it securely.
                  </div>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/v1/keys"
                  description="List all active API keys for the tenant."
                  auth="JWT"
                >
                  <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                    Dashboard session only — not available via API key
                    authentication.
                  </div>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "keys": [
    {
      "key_id": "key_a1b2c3d4",
      "hint": "wos_••••••••abcd",
      "label": "CI/CD pipeline",
      "status": "active",
      "created_at": "2025-03-09T12:00:00.000Z",
      "last_used_at": "2025-03-09T14:30:00.000Z"
    }
  ]
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="DELETE"
                  path="/v1/keys/:id"
                  description="Revoke an API key. All requests using it will return 401."
                  auth="JWT"
                >
                  <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                    Dashboard session only — not available via API key
                    authentication.
                  </div>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "revoked": true,
  "id": "key_a1b2c3d4"
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="POST"
                  path="/v1/keys/revoke-all"
                  description="Revoke all active API keys. Requires explicit confirmation."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Request body
                  </p>
                  <CodeBlock>
                    {`{
  "accept_potential_outage": true
}`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "revoked_count": 3
}`}
                  </CodeBlock>
                </Endpoint>
              </Section>

              {/* Billing & Usage */}
              <Section id="billing" title="Billing & usage">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    Usage is metered on two dimensions: <strong>ingest events</strong>{" "}
                    (count of accepted webhooks) and <strong>storage</strong>{" "}
                    (GB-months of retained payloads).
                  </p>
                  <ul class="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Free:</strong> 5 webhooks per month, 1-day
                      retention, no credit card required.
                    </li>
                    <li>
                      <strong>Activated:</strong> $25 one-time activation
                      (includes $25 in credits). After credits are consumed,
                      $0.03 per webhook and $0.10 per GB-month. Unlocks 7-day
                      webhook retention.
                    </li>
                    <li>
                      <strong>Business:</strong> Custom limits, retention, and
                      support.
                    </li>
                  </ul>
                </div>

                <Endpoint
                  method="GET"
                  path="/v1/billing/summary"
                  description="Get current billing period usage and cost summary."
                  auth="JWT or API key"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · activated account
                  </p>
                  <CodeBlock>
                    {`{
  "period_start": "2026-03-01",
  "period_end": "2026-03-31",
  "ingest_events_count": 142,
  "current_storage_mb": 1250.5,
  "storage_mb_days": 8753.5,
  "ingest_events_cost_usd": 7.10,
  "storage_cost_usd": 4.28,
  "total_cost_usd": 11.38,
  "tier": "activated",
  "subscription_status": "active",
  "free": null,
  "activated": {
    "activation_fee_usd": 25,
    "credits_included_usd": 25,
    "retention_days": 7
  }
}`}
                  </CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · free account
                  </p>
                  <CodeBlock>
                    {`{
  "...": "...",
  "tier": "free",
  "subscription_status": "not_activated",
  "free": {
    "ingest_events_limit": 5,
    "ingest_events_used": 2,
    "ingest_events_remaining": 3
  },
  "activated": null
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="GET"
                  path="/v1/billing/daily"
                  description="Get daily usage breakdown for the last 30 days."
                  auth="JWT"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>
                    {`{
  "daily": [
    {
      "date": "2025-03-09",
      "ingest_count": 15,
      "storage_mb": 1250.5
    }
  ]
}`}
                  </CodeBlock>
                </Endpoint>

                <Endpoint
                  method="POST"
                  path="/v1/billing/portal"
                  description="Create a Stripe Customer Portal session for managing payment methods."
                  auth="JWT"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>{`{ "url": "https://billing.stripe.com/..." }`}</CodeBlock>
                </Endpoint>

                <Endpoint
                  method="POST"
                  path="/v1/billing/checkout"
                  description="Create a Stripe Checkout session to add a payment method and activate pay-as-you-go billing. Only available for free-tier tenants."
                  auth="JWT"
                >
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 200
                  </p>
                  <CodeBlock>{`{ "url": "https://checkout.stripe.com/..." }`}</CodeBlock>
                  <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Response · 409 CONFLICT
                  </p>
                  <p>
                    Returned when the tenant is already on the activated tier.
                    Re-run from a free-tier account or use{" "}
                    <code>/v1/billing/portal</code> to manage an existing
                    payment method.
                  </p>
                  <CodeBlock>{`{ "error": { "code": "CONFLICT", "message": "Billing is already activated" } }`}</CodeBlock>
                </Endpoint>
              </Section>

              {/* Error Handling */}
              <Section id="errors" title="Error handling">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    All errors follow a consistent JSON format with an{" "}
                    <code>error</code> object containing a machine-readable{" "}
                    <code>code</code> and human-readable <code>message</code>.
                  </p>
                </div>
                <CodeBlock>
                  {`{
  "error": {
    "code": "NOT_FOUND",
    "message": "Endpoint not found"
  }
}`}
                </CodeBlock>
                <p class="text-sm leading-7 text-slate-700">
                  A <code>details</code> field may be included for validation
                  errors with additional context.
                </p>

                <div class="overflow-x-auto">
                  <table class="w-full text-sm">
                    <thead>
                      <tr class="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                        <th class="pb-2 pr-4">HTTP</th>
                        <th class="pb-2 pr-4">Code</th>
                        <th class="pb-2">Description</th>
                      </tr>
                    </thead>
                    <tbody class="text-slate-700">
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">400</td>
                        <td class="py-2 pr-4">VALIDATION</td>
                        <td class="py-2">
                          Invalid request body or parameters (management API)
                        </td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">401</td>
                        <td class="py-2 pr-4">UNAUTHORIZED</td>
                        <td class="py-2">
                          Missing or invalid token / secret
                        </td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">402</td>
                        <td class="py-2 pr-4">BILLING_BLOCKED</td>
                        <td class="py-2">Activated tenant past due (billing blocked)</td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">402</td>
                        <td class="py-2 pr-4">FREE_TIER_LIMIT_REACHED</td>
                        <td class="py-2">Ingest only — free tier monthly cap exhausted</td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">403</td>
                        <td class="py-2 pr-4">FORBIDDEN</td>
                        <td class="py-2">Account disabled</td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">404</td>
                        <td class="py-2 pr-4">NOT_FOUND</td>
                        <td class="py-2">Resource does not exist</td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">409</td>
                        <td class="py-2 pr-4">CONFLICT</td>
                        <td class="py-2">
                          Duplicate or limit reached (e.g. max 2 active secrets per endpoint)
                        </td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">413</td>
                        <td class="py-2 pr-4">PAYLOAD_TOO_LARGE</td>
                        <td class="py-2">Exceeds payload cap</td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">429</td>
                        <td class="py-2 pr-4">RATE_LIMITED</td>
                        <td class="py-2">
                          Too many requests (see <code>Retry-After</code>{" "}
                          header)
                        </td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">410</td>
                        <td class="py-2 pr-4">GONE</td>
                        <td class="py-2">Tenant cancelled or deactivated (ingest)</td>
                      </tr>
                      <tr class="border-b border-slate-100">
                        <td class="py-2 pr-4 font-mono">503</td>
                        <td class="py-2 pr-4">SERVICE_UNAVAILABLE</td>
                        <td class="py-2">Tenant is being provisioned (ingest)</td>
                      </tr>
                      <tr>
                        <td class="py-2 pr-4 font-mono">500</td>
                        <td class="py-2 pr-4">INTERNAL</td>
                        <td class="py-2">Unexpected server error</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Section>

              {/* Rate Limiting */}
              <Section id="rate-limiting" title="Rate limiting">
                <div class="space-y-4 text-sm leading-7 text-slate-700">
                  <p>
                    Ingest endpoints enforce rate limits at two levels to protect
                    system stability:
                  </p>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <div class="rounded-lg border border-slate-200 p-4">
                      <p class="font-semibold text-slate-900">Per-tenant</p>
                      <p class="mt-1 text-xs text-slate-500">
                        1,000 requests per minute, shared across all endpoints.
                      </p>
                    </div>
                    <div class="rounded-lg border border-slate-200 p-4">
                      <p class="font-semibold text-slate-900">Per-endpoint</p>
                      <p class="mt-1 text-xs text-slate-500">
                        100 requests per minute per endpoint.
                      </p>
                    </div>
                  </div>
                  <p>
                    When rate-limited, the response includes a{" "}
                    <code>Retry-After</code> header with the number of seconds
                    to wait before retrying.
                  </p>
                  <CodeBlock>
                    {`HTTP/1.1 429 Too Many Requests
Retry-After: 5
Content-Type: application/json

{
  "error": {
    "code": "RATE_LIMITED",
    "message": "Rate limit exceeded. Retry after 5 seconds."
  }
}`}
                  </CodeBlock>
                </div>
              </Section>
            </div>

            <div class="mt-12 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-600">
              See a problem with these docs?{" "}
              <a
                href="mailto:support@webhookstorage.com?subject=API%20docs%20feedback"
                class="font-medium text-teal-700 underline hover:text-teal-900"
              >
                Please let us know
              </a>
            </div>
          </main>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
